import React from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get } from 'lodash';
import { H2 } from 'components/core/Typography';
import { Breadcrumbs } from 'components/core/Breadcrumbs';
import { Card } from 'components/legacy/Card';
import { Accordion } from 'components/search/Accordion';
import { MarkdownWrapper } from 'components/search/MarkdownWrapper';
import TrustPilotReviewSection from 'components/landing/TrustPilotReviewSection/TrustPilotReviewSection';
import BestPricedUnits from './BestPricedUnits';
import CityLinks from './CityLinks';
import NearbyCities from './NearbyCities';
import { AveragePricesContent } from './AveragePricesContent';
import { amenityObject } from 'config/content';
import { INTERNAL_PAGE_TYPES } from '../../../config/constants';

import './searchContent.scss';

export default function SearchContent({
	content,
	location,
	pageType,
	faqContent,
	faqShowing,
	averageMonthlyUnitPrice,
	amenityType,
	cityUrl,
	stateUrl,
	baseUrl,
	searchType,
}) {
	const { seoBodyJson } = content;

	const hasTpReviews = !!content?.tpReviews?.length;
	const { city, state } = location;

	return (
		<div className="search-content">
			<H2
				className="seo-content-header"
				size="mediumLarge"
				weight="extraBold"
				color="darkGray"
			>
				{`${city}, ${state} ${amenityObject[amenityType]} Storage Information`}
			</H2>
			{seoBodyJson && (
				<Card>
					<MarkdownWrapper content={seoBodyJson} />
				</Card>
			)}
			{faqShowing && (
				<Card>
					<Accordion
						accordionTitle={`Frequently Asked Questions About ${city}, ${state} Self Storage`}
						content={faqContent}
						isFAQ
						className="faq-content"
						preserve
					/>
				</Card>
			)}
			{!isEmpty(averageMonthlyUnitPrice) && (
				<AveragePricesContent
					data={averageMonthlyUnitPrice}
					city={city}
				/>
			)}
			{pageType === INTERNAL_PAGE_TYPES.CITY_LANDING_PAGE &&
				!isEmpty(location) && (
					<Card className="best-priced-units">
						<div className="card-content">
							<BestPricedUnits
								location={location}
								pricingStats={content.pricingStats}
							/>
						</div>
					</Card>
				)}
			{!isEmpty(get(content, 'nearbyCities')) && (
				<NearbyCities
					title={`Browse Self Storage in Nearby ${location.fullState} cities`}
					links={content.nearbyCities}
				/>
			)}
			{!isEmpty(get(content, 'cityLinks.amenities')) && (
				<CityLinks
					title={`Browse ${location.city} Self Storage by Amenity`}
					links={content.cityLinks.amenities}
					city={location.city}
				/>
			)}
			{!isEmpty(get(content, 'cityLinks.vehicleStorage')) && (
				<CityLinks
					title={`Browse ${location.city} Self Storage by Vehicle`}
					links={content.cityLinks.vehicleStorage}
					city={location.city}
				/>
			)}

			{hasTpReviews && (
				<Card>
					<TrustPilotReviewSection
						reviews={content.tpReviews}
						isHomePage={false}
					/>
				</Card>
			)}

			<Breadcrumbs
				baseUrl={baseUrl}
				cityUrl={cityUrl}
				stateUrl={stateUrl}
				searchType={searchType}
				location={location}
				isCLPPage={true}
			/>
		</div>
	);
}

SearchContent.propTypes = {
	content: PropTypes.shape({
		seoBodyJson: PropTypes.string,
		tpReviews: PropTypes.array,
		pricingStats: PropTypes.object,
		nearbyCities: PropTypes.arrayOf(PropTypes.object),
		cityLinks: PropTypes.object,
		bestPricedUnits: PropTypes.arrayOf(PropTypes.object),
	}),
	location: PropTypes.shape({
		city: PropTypes.string,
		state: PropTypes.string,
		fullState: PropTypes.string,
	}).isRequired,
	pageType: PropTypes.string,
	searchParams: PropTypes.shape({
		type: PropTypes.string,
		vehicle: PropTypes.string,
	}).isRequired,
	faqContent: PropTypes.arrayOf(PropTypes.object),
	faqShowing: PropTypes.bool,
	averageMonthlyUnitPrice: PropTypes.object,
	amenityType: PropTypes.string,
	cityUrl: PropTypes.string,
	stateUrl: PropTypes.string,
	baseUrl: PropTypes.string,
	searchType: PropTypes.string,
};
